import type { CaseRecord } from '@/shared/domain/case';
import type { Vocabulary } from '@/shared/domain/vocabulary';

/* Where a case row opens. A case list is a list of decisions waiting to be made,
   so a row lands on the tab holding the decision rather than on a summary the
   reader would have to navigate out of again.

   `audit` is never landed on — nothing about a timeline is a decision — but it
   is one of the workspace's tabs, so it belongs in the type that names them. */

export type CaseTab =
  | 'overview'
  | 'mri'
  | 'gaps'
  | 'communication'
  | 'routing'
  | 'audit';

export function caseTab(record: CaseRecord): CaseTab {
  // A routed case has no MRI work left on this desk; the decision to explain is
  // why it was sent elsewhere.
  if (record.queue !== 'work') return 'routing';
  if (record.disposition === 'conflict') return 'gaps';
  if (record.disposition === 'clarification' && (record.gaps ?? 0) > 2) return 'communication';
  if (record.disposition === 'ready') return 'mri';
  return 'overview';
}

/** One tab of one case. The workspace builds its tab bar from this, and a screen
    linking into a particular tab uses it rather than composing the path by
    hand — the route shape is stated once, here and in the router. */
export function caseTabPath(id: string, tab: CaseTab): string {
  return `/case/${id}/${tab}`;
}

export function caseRoute(record: CaseRecord): string {
  return caseTabPath(record.id, caseTab(record));
}

/** Everything a case row can be searched on, joined. Owner falls back to the
    word an operator would actually type when looking for unclaimed work.

    Takes the vocabulary because three of the nine parts are words the record no
    longer carries. That is the point: an operator searching a German cockpit for
    `Rückfrage` has to match the German label, and a search index built from
    English keys would find nothing. The words are resolved at search time, in
    whatever language the screen is currently in. */
export function caseSearchText(record: CaseRecord, vocabulary: Vocabulary): string {
  return [
    record.id,
    record.order ?? '',
    vocabulary.caseCustomer(record),
    record.tenant ?? '',
    record.offering,
    vocabulary.caseState(record),
    vocabulary.desk(record.deskKey),
    record.owner ?? 'unassigned',
    vocabulary.caseAction(record),
  ].join(' ');
}
